// src/pages/CoveragePage.jsx
import { AddIcon, EditIcon, RepeatIcon } from '@chakra-ui/icons';
import {
    Badge, Box, Button, Card, CardBody, Checkbox, Divider, Flex, FormControl, FormErrorMessage,
    Heading, HStack, IconButton, Input, Menu, MenuButton, MenuItem, MenuList, Select, Spinner,
    Switch, Table, Tbody, Td, Text, Textarea, Th, Thead, Tr, useDisclosure, useToast, VStack
} from '@chakra-ui/react';
import { useEffect, useMemo, useState } from 'react';
import { FiMoreVertical, FiRotateCcw, FiTrash2 } from 'react-icons/fi';
import {
    createCptIcdCoverage, deleteCptIcdCoverage, getCptIcdCoverages,
    restoreCptIcdCoverage, updateCptIcdCoverage
} from '../../../api';
import AccessDenied from '../../../components/AccessDenied';
import { hasPermission } from '../../../Utils';

const PageLimit = 20;

// 🔎 patterns
const CPT_RX = /^(\d{5}|[A-Z0-9]{5})$/;           // 88305, 1234F, 0123T, A12B3
const ICD10_RX = /^[A-TV-Z][0-9A-Z][0-9A-Z](?:\.[0-9A-Z]{1,4})?$/i; // K52.9, E11.9, Z00.00

export default function CoveragePage() {
    const toast = useToast();

    const canView = hasPermission('viewCptIcdCoverage');
    const canCreate = hasPermission('createCptIcdCoverage');
    const canUpdate = hasPermission('updateCptIcdCoverage');
    const canDelete = hasPermission('deleteCptIcdCoverage');
    const canRestore = hasPermission('restoreCptIcdCoverage');

    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [pages, setPages] = useState(1);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // filters
    const [q, setQ] = useState('');
    const [cpt, setCpt] = useState('');
    const [icd10, setIcd10] = useState('');
    const [isActive, setIsActive] = useState('');
    const [includeDeleted, setIncludeDeleted] = useState(false);
    const [sort, setSort] = useState('cpt,icd10');

    const { isOpen, onOpen, onClose } = useDisclosure();
    const [editing, setEditing] = useState(null);
    const [saving, setSaving] = useState(false);
    const [deletingId, setDeletingId] = useState(null);
    const [restoringId, setRestoringId] = useState(null);

    const [form, setForm] = useState({
        cpt: '', icd10: '', cashPrice: '', coveragePolicy: '', isActive: true
    });
    const [errors, setErrors] = useState({});

    const queryString = useMemo(() => {
        const p = new URLSearchParams();
        p.set('page', String(page));
        p.set('limit', String(PageLimit));
        if (q) p.set('q', q);
        if (cpt) p.set('cpt', cpt);
        if (icd10) p.set('icd10', icd10);
        if (isActive) p.set('isActive', isActive);
        if (includeDeleted) p.set('includeDeleted', 'true');
        if (sort) p.set('sort', sort);
        return p.toString();
    }, [page, q, cpt, icd10, isActive, includeDeleted, sort]);

    const load = async () => {
        if (!canView) return;
        try {
            setLoading(true);
            const res = await getCptIcdCoverages(decodeURIComponent(queryString));
            if (res.success) {
                const payload = res?.data || res;
                setItems(payload.items || []);
                setTotal(payload.total || 0);
                setPage(payload.page || 1);
                setPages(payload.pages || 1);
            } else {
                setError(res.error || res.message);
            }
        } catch (err) {
            toast({ status: 'error', title: 'Load failed', description: err.message });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [queryString, canView]);

    const resetForm = () => {
        setForm({ cpt: '', icd10: '', cashPrice: '', coveragePolicy: '', isActive: true });
        setErrors({});
        setEditing(null);
    };

    const openCreate = () => {
        if (!canCreate) return toast({ status: 'warning', title: 'No permission to create' });
        resetForm(); onOpen();
    };

    const openEdit = (doc) => {
        if (!canUpdate) return toast({ status: 'warning', title: 'No permission to edit' });
        setEditing(doc);
        setForm({
            cpt: doc.cpt || '',
            icd10: doc.icd10 || '',
            cashPrice: doc.cashPrice ?? '',
            coveragePolicy: doc.coveragePolicy || '',
            isActive: !!doc.isActive,
        });
        setErrors({});
        onOpen();
    };

    // ✅ validation
    const validate = (f) => {
        const e = {};
        const cpt = String(f.cpt || '').toUpperCase().trim();
        const icd = String(f.icd10 || '').toUpperCase().trim();
        const price = f.cashPrice === '' ? '' : Number(f.cashPrice);

        if (!cpt) e.cpt = 'CPT is required';
        else if (!CPT_RX.test(cpt)) e.cpt = 'CPT must be 5 chars (digits or A–Z/0–9)';

        if (!icd) e.icd10 = 'ICD-10 is required';
        else if (!ICD10_RX.test(icd)) e.icd10 = 'Invalid ICD-10 format (e.g., K52.9)';

        // if (f.cashPrice === '') e.cashPrice = 'Cash price required';
        // else if (Number.isNaN(price)) e.cashPrice = 'Cash price must be a number';
        // else if (price < 0) e.cashPrice = 'Cash price cannot be negative';

        if (f.coveragePolicy && String(f.coveragePolicy).length > 2000) {
            e.coveragePolicy = 'Max 2000 characters';
        }
        return e;
    };

    const onSave = async () => {
        // normalize to uppercase for codes
        const next = {
            ...form,
            cpt: String(form.cpt).toUpperCase(),
            icd10: String(form.icd10).toUpperCase(),
        };
        const e = validate(next);
        setErrors(e);
        if (Object.keys(e).length) {
            toast({ status: 'error', title: 'Please fix form errors' });
            return;
        }

        try {
            setSaving(true);
            const payload = {
                cpt: next.cpt.trim(),
                icd10: next.icd10.trim(),
                cashPrice: next.cashPrice === '' ? 0 : Number(next.cashPrice),
                coveragePolicy: String(next.coveragePolicy || '').trim(),
                isActive: !!next.isActive,
            };

            const res = editing
                ? await updateCptIcdCoverage(editing._id, payload)
                : await createCptIcdCoverage(payload);

            if (res?.success === false) throw new Error(res?.message || 'Save failed');

            toast({ status: 'success', title: editing ? 'Updated' : 'Created' });
            onClose(); resetForm(); load();
        } catch (err) {
            toast({ status: 'error', title: 'Save failed', description: err.message });
        } finally {
            setSaving(false);
        }
    };

    const onSoftDelete = async (id) => {
        if (!canDelete) return toast({ status: 'warning', title: 'No permission to delete' });
        try {
            setDeletingId(id);
            const res = await deleteCptIcdCoverage(id, 'User requested removal');
            if (res?.success === false) throw new Error(res?.message || 'Delete failed');
            toast({ status: 'success', title: 'Soft deleted' });
            load();
        } catch (err) {
            toast({ status: 'error', title: 'Delete failed', description: err.message });
        } finally {
            setDeletingId(null);
        }
    };

    const onRestore = async (id) => {
        if (!canRestore) return toast({ status: 'warning', title: 'No permission to restore' });
        try {
            setRestoringId(id);
            const res = await restoreCptIcdCoverage(id);
            if (res?.success === false) throw new Error(res?.message || 'Restore failed');
            toast({ status: 'success', title: 'Restored' });
            load();
        } catch (err) {
            toast({ status: 'error', title: 'Restore failed', description: err.message });
        } finally {
            setRestoringId(null);
        }
    };

    if (!canView) return <AccessDenied msg="You do not have permission to view coverage mappings." />;
    if (error) return <AccessDenied msg={error} />;

    return (
        <Box p={5}>
            <Flex justify="space-between" align="center" mb={4}>
                <Heading size="lg">CPT ↔ ICD-10 Coverage</Heading>
                <HStack>
                    {canCreate && <Button size={'xs'} leftIcon={<AddIcon />} colorScheme="blue" onClick={openCreate}>New Mapping</Button>}
                    <IconButton size={'xs'} aria-label="refresh" icon={<RepeatIcon />} onClick={load} />
                </HStack>
            </Flex>

            {/* Filters */}
            <Card mb={4}>
                <CardBody>
                    <HStack spacing={3} flexWrap="wrap">
                        <Input placeholder="Search any" value={q} onChange={(e) => { setPage(1); setQ(e.target.value); }} maxW="240px" />
                        <Input placeholder="CPT" value={cpt} onChange={(e) => { setPage(1); setCpt(e.target.value); }} maxW="140px" />
                        <Input placeholder="ICD-10" value={icd10} onChange={(e) => { setPage(1); setIcd10(e.target.value); }} maxW="160px" />
                        <Select value={isActive} onChange={(e) => { setPage(1); setIsActive(e.target.value); }} maxW="160px">
                            <option value="">Any status</option>
                            <option value="true">Active</option>
                            <option value="false">Inactive</option>
                        </Select>
                        <Checkbox isChecked={includeDeleted} onChange={(e) => { setPage(1); setIncludeDeleted(e.target.checked); }}>
                            Include deleted
                        </Checkbox>
                        <Select value={sort} onChange={(e) => { setPage(1); setSort(e.target.value); }} maxW="220px">
                            <option value="cpt,icd10">Sort: CPT, ICD-10</option>
                            <option value="-createdAt">Newest</option>
                        </Select>
                    </HStack>
                </CardBody>
            </Card>

            {/* Table */}
            <Card>
                <CardBody p={0}>
                    {loading ? (
                        <Flex align="center" justify="center" py={10}><Spinner /></Flex>
                    ) : (
                        <Table size="sm">
                            <Thead>
                                <Tr>
                                    <Th w="36px">#</Th>
                                    <Th>CPT</Th>
                                    <Th>ICD-10</Th>
                                    <Th isNumeric>Cash Price</Th>
                                    <Th>Coverage Policy</Th>
                                    <Th>Status</Th>
                                    <Th w="120px">Actions</Th>
                                </Tr>
                            </Thead>
                            <Tbody>
                                {items.map((it, idx) => (
                                    <Tr key={it._id} _hover={{ bg: 'gray.50' }}>
                                        <Td>{(page - 1) * PageLimit + idx + 1}</Td>
                                        <Td><Text fontWeight="semibold">{it.cpt}</Text></Td>
                                        <Td>{it.icd10}</Td>
                                        <Td isNumeric>{typeof it.cashPrice === 'number' ? it.cashPrice.toFixed(2) : '0.00'}</Td>
                                        <Td><Text noOfLines={1} maxW="360px">{it.coveragePolicy || '—'}</Text></Td>
                                        <Td>{it.deletedAt ? <Badge colorScheme="red">Deleted</Badge> : (it.isActive ? <Badge colorScheme="green">Active</Badge> : <Badge>Inactive</Badge>)}</Td>
                                        <Td>
                                            <HStack spacing={1} justify="flex-end">
                                                {!it.deletedAt ? (
                                                    <>
                                                        {canUpdate && (
                                                            <IconButton aria-label="edit" size="sm" icon={<EditIcon />} onClick={() => openEdit(it)} />
                                                        )}
                                                        {canDelete && (
                                                            <Menu>
                                                                <MenuButton as={IconButton} aria-label="more" size="sm" icon={<FiMoreVertical />} />
                                                                <MenuList>
                                                                    <MenuItem icon={<FiTrash2 />} onClick={() => onSoftDelete(it._id)} isDisabled={deletingId === it._id}>
                                                                        {deletingId === it._id ? 'Deleting…' : 'Soft delete'}
                                                                    </MenuItem>
                                                                </MenuList>
                                                            </Menu>
                                                        )}
                                                    </>
                                                ) : (
                                                    canRestore && (
                                                        <Button size="sm" leftIcon={<FiRotateCcw />} onClick={() => onRestore(it._id)} isLoading={restoringId === it._id}>
                                                            Restore
                                                        </Button>
                                                    )
                                                )}
                                            </HStack>
                                        </Td>
                                    </Tr>
                                ))}
                                {!items.length && !loading && (
                                    <Tr><Td colSpan={7}><Flex justify="center" py={10}><Text color="gray.500">No mappings</Text></Flex></Td></Tr>
                                )}
                            </Tbody>
                        </Table>
                    )}
                </CardBody>
            </Card>

            {/* Pagination */}
            <Flex align="center" justify="space-between" mt={4}>
                <Text fontSize="sm" color="gray.600">{total} total • Page {page} of {pages}</Text>
                <HStack>
                    <Button size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} isDisabled={page <= 1}>Prev</Button>
                    <Button size="sm" onClick={() => setPage(p => Math.min(pages, p + 1))} isDisabled={page >= pages}>Next</Button>
                </HStack>
            </Flex>

            {/* Modal */}
            {isOpen && (
                <Box position="fixed" inset="0" bg="blackAlpha.500" display="flex" alignItems="center" justifyContent="center" zIndex={1000}>
                    <Card w="720px" maxW="96vw">
                        <CardBody>
                            <Heading size="md" mb={3}>{editing ? 'Edit Mapping' : 'New Mapping'}</Heading>
                            <VStack align="stretch" spacing={3}>
                                <HStack>
                                    <FormControl isInvalid={!!errors.cpt}>
                                        <Text fontSize="sm" mb={1}>CPT</Text>
                                        <Input
                                            value={form.cpt}
                                            onChange={e => setForm(f => ({ ...f, cpt: e.target.value.toUpperCase() }))}
                                            onBlur={() => setErrors(prev => ({ ...prev, ...validate({ ...form, cpt: form.cpt }) }))}
                                        />
                                        <FormErrorMessage>{errors.cpt}</FormErrorMessage>
                                    </FormControl>

                                    <FormControl isInvalid={!!errors.icd10}>
                                        <Text fontSize="sm" mb={1}>ICD-10</Text>
                                        <Input
                                            value={form.icd10}
                                            onChange={e => setForm(f => ({ ...f, icd10: e.target.value.toUpperCase() }))}
                                            onBlur={() => setErrors(prev => ({ ...prev, ...validate({ ...form, icd10: form.icd10 }) }))}
                                        />
                                        <FormErrorMessage>{errors.icd10}</FormErrorMessage>
                                    </FormControl>

                                    <FormControl isInvalid={!!errors.cashPrice} w="200px">
                                        <Text fontSize="sm" mb={1}>Cash Price</Text>
                                        <Input
                                            type="number"
                                            step="0.01"
                                            min="0"
                                            value={form.cashPrice}
                                            onChange={e => setForm(f => ({ ...f, cashPrice: e.target.value }))}
                                            onBlur={() => setErrors(prev => ({ ...prev, ...validate({ ...form }) }))}
                                        />
                                        <FormErrorMessage>{errors.cashPrice}</FormErrorMessage>
                                    </FormControl>
                                </HStack>

                                <FormControl isInvalid={!!errors.coveragePolicy}>
                                    <Text fontSize="sm" mb={1}>Coverage Policy</Text>
                                    <Textarea
                                        rows={3}
                                        value={form.coveragePolicy}
                                        onChange={e => setForm(f => ({ ...f, coveragePolicy: e.target.value }))}
                                        onBlur={() => setErrors(prev => ({ ...prev, ...validate({ ...form }) }))}
                                    />
                                    <FormErrorMessage>{errors.coveragePolicy}</FormErrorMessage>
                                </FormControl>

                                <HStack>
                                    <Text>Active</Text>
                                    <Switch
                                        isChecked={form.isActive}
                                        onChange={e => setForm(f => ({ ...f, isActive: e.target.checked }))}
                                    />
                                </HStack>
                            </VStack>

                            <Divider my={4} />
                            <HStack justify="flex-end">
                                <Button size={'xs'} variant="ghost" onClick={() => { onClose(); resetForm(); }} isDisabled={saving}>Cancel</Button>
                                <Button
                                    size={'xs'}
                                    colorScheme="blue"
                                    onClick={onSave}
                                    isLoading={saving}
                                    isDisabled={(editing && !canUpdate) || (!editing && !canCreate) || Object.keys(errors).length > 0}
                                >
                                    {editing ? 'Save Changes' : 'Create'}
                                </Button>
                            </HStack>
                        </CardBody>
                    </Card>
                </Box>
            )}
        </Box>
    );
}
